import { useCallback, useEffect, useMemo } from 'react';

import { useFormListValidation } from 'core';
import { ICredential, useCreatePaymentMethod } from 'core/Api';

import { CheckoutForms } from '../checkout.types';

type UseCreateCredential = {
  onCredentialChange: (credentialId?: ICredential['uuid']) => void;
};

const useCreateCredential = ({ onCredentialChange }: UseCreateCredential) => {
  const { mutateAsync: createCard, isPending: isCreateCardLoading } = useCreatePaymentMethod();

  const { useGetForm } = useFormListValidation<CheckoutForms>();

  /** Form for the Existing Credentials */
  const existingCredentialsForm = useGetForm('existing_credentials');

  const stripeCardForm = useGetForm('stripe_credit_card');
  const stripeCvvForm = useGetForm('stripe_cvv');
  const stgForm = useGetForm('shop_to_give_enrollment');
  const donorInfoForm = useGetForm('donor_info');

  const credentialId = useMemo(
    () => existingCredentialsForm?.values?.credentialId,
    [existingCredentialsForm?.values?.credentialId]
  );

  const createCredential = useCallback(async () => {
    // Guard against neither a credential ID nor a token being present
    if (!stripeCardForm?.values?.stripeComplete && !credentialId) {
      throw new Error('Select a valid payment method');
    }

    // If StripeCard form is available
    if (stripeCardForm) {
      if (stripeCardForm.validate().hasErrors) {
        return;
      }
      const { token, shouldSaveCard } = await stripeCardForm.getTransformedValues();

      // If neither a token nor a credential ID is present, throw an error
      if (!token) {
        throw new Error('Invalid card');
      }

      // If the user does not has a credential ID and we have a token, create a new card
      if (!credentialId && token) {
        const credential = await createCard({
          email: donorInfoForm?.values?.email ?? null,
          card_token: token,
          save_for_future_use: shouldSaveCard ?? false,
          enroll_in_shop_to_give: stgForm?.values.terms ?? false,
        });
        return { uuid: credential.uuid };
      }
    } else {
      /** Run Validation For Existing Credential Forms  */
      existingCredentialsForm?.validate();

      if (credentialId) {
        if (!stripeCvvForm) throw new Error('Invalid CVV');

        const { token: cvvToken } = await stripeCvvForm.getTransformedValues();
        if (!cvvToken) {
          stripeCvvForm?.setFieldError('token', 'Invalid CVV');
          throw new Error('Invalid CVV');
        }
        return {
          uuid: credentialId,
          cvvToken,
        };
      } else {
        throw new Error('Select a valid payment method');
      }
    }
  }, [
    stripeCardForm,
    credentialId,
    existingCredentialsForm,
    createCard,
    donorInfoForm?.values?.email,
    stgForm?.values.terms,
    stripeCvvForm,
  ]);

  useEffect(() => {
    onCredentialChange(credentialId);
  }, [onCredentialChange, credentialId]);

  return {
    createCredential,
    isCreateCardLoading,
  };
};

export { useCreateCredential };
